import { useEffect, useState, useCallback } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Card, CardContent } from '../ui/card';
import api from '../../services/api';
import { toast } from 'sonner';
import { useBusinessTimezone, getTodayInTz, formatDateOnlyDisplay } from '../../utils/businessTime';
import {
  Plus,
  Edit,
  Trash2,
  Calendar,
  MapPin,
  Clock,
  Users,
  Home,
  Plane,
  Upload,
  Eye,
  Loader2,
  CheckCircle,
  XCircle,
  HelpCircle,
  Shield,
  UserPlus,
  Lock,
  Trophy,
  Flag,
  Archive,
} from 'lucide-react';
// iter277+iter279 — extracted sub-dialogs live in ./tournament-games/
import GameRsvpsDialog from './tournament-games/GameRsvpsDialog';
import GameResultDialog from './tournament-games/GameResultDialog';
import BulkUploadGamesDialog from './tournament-games/BulkUploadGamesDialog';
import EditGameDialog from './tournament-games/EditGameDialog';
import ManagePlayersDialog from './tournament-games/ManagePlayersDialog';
import CreateGameDialog from './tournament-games/CreateGameDialog';
import SelectPlayersDialog from './tournament-games/SelectPlayersDialog';
import { GAME_CATEGORIES as GAME_CATEGORIES_SHARED, GAME_CATEGORY_COLORS as GAME_CATEGORY_COLORS_SHARED } from './tournament-games/constants';

const RSVP_ICONS = {
  yes: <CheckCircle className="h-4 w-4 text-green-600" />,
  no: <XCircle className="h-4 w-4 text-red-600" />,
  tentative: <HelpCircle className="h-4 w-4 text-yellow-600" />,
};

const RSVP_COLORS = {
  yes: 'bg-green-100 text-green-700',
  no: 'bg-red-100 text-red-700',
  tentative: 'bg-yellow-100 text-yellow-700',
};

// iter279 — moved into ./tournament-games/constants.js so the extracted
// EditGameDialog (and the future CreateGameDialog) can pull them
// without prop-drilling. Local aliases keep existing in-file usages
// unchanged.
const GAME_CATEGORIES = GAME_CATEGORIES_SHARED;
const GAME_CATEGORY_COLORS = GAME_CATEGORY_COLORS_SHARED;

export const TournamentGames = ({ tournament, onClose }) => {
  const businessTz = useBusinessTimezone();
  const [games, setGames] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showRsvpDialog, setShowRsvpDialog] = useState(false);
  const [showBulkUploadDialog, setShowBulkUploadDialog] = useState(false);
  const [showManagePlayersDialog, setShowManagePlayersDialog] = useState(false);
  const [showSelectPlayersDialog, setShowSelectPlayersDialog] = useState(false);
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [gameResult, setGameResult] = useState('');
  const [gameResultNotes, setGameResultNotes] = useState('');
  const [selectedGame, setSelectedGame] = useState(null);
  const [gameRsvps, setGameRsvps] = useState([]);
  const [rsvpsWithCounts, setRsvpsWithCounts] = useState([]);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState([]);
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [ageFilter, setAgeFilter] = useState('all');
  const [deleteGameDialogOpen, setDeleteGameDialogOpen] = useState(false);
  const [gameToDelete, setGameToDelete] = useState(null);
  const [addPlayerWarning, setAddPlayerWarning] = useState(null);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  
  const [formData, setFormData] = useState({
    age_category: '',
    age_categories: [], // For internal games - multiple categories
    is_internal: false, // Internal practice game checkbox
    game_date: '',
    start_time: '',
    reporting_time: '',  // When players should arrive
    rsvp_deadline: '',   // Deadline for RSVPs
    location: '',
    location_url: '',    // Google Maps URL for the location
    opponent_team: '',
    game_type: 'home',
    game_category: '',  // practice, league, competitive, etc.
    notes: '',
    team_id: '', // Our team for this game
  });
  
  const [bulkData, setBulkData] = useState('');

  // Get teams filtered by selected age category in form
  const availableTeams = teams.filter(t => t.age_category === formData.age_category);

  const fetchGames = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/tournaments/${tournament.id}/games`);
      setGames(res.data);
    } catch (error) {
      toast.error('Failed to load games');
    } finally {
      setLoading(false);
    }
  }, [tournament.id]);

  const fetchTeams = useCallback(async () => {
    try {
      const res = await api.get(`/tournaments/${tournament.id}/teams`);
      setTeams(res.data);
    } catch (error) {
      console.error('Failed to load teams');
    }
  }, [tournament.id]);

  useEffect(() => {
    if (tournament?.id) {
      fetchGames();
      fetchTeams();
    }
  }, [tournament?.id, fetchGames, fetchTeams]);

  const resetForm = () => {
    setFormData({
      age_category: tournament?.age_categories?.[0] || '',
      age_categories: [],
      is_internal: false,
      game_date: tournament?.start_date || '',
      start_time: '10:00',
      reporting_time: '',
      rsvp_deadline: '',
      location: '',
      location_url: '',
      opponent_team: '',
      game_type: 'home',
      game_category: '',
      notes: '',
      team_id: '',
    });
  };

  const handleCreateGame = async () => {
    // Validate based on internal vs external game
    if (formData.is_internal) {
      if (!formData.age_categories || formData.age_categories.length === 0) {
        toast.error('Please select at least one age category for internal game');
        return;
      }
    } else {
      if (!formData.age_category) {
        toast.error('Please select an age category');
        return;
      }
      if (!formData.opponent_team) {
        toast.error('Please enter the opponent team');
        return;
      }
    }
    
    if (!formData.game_date || !formData.start_time || !formData.location) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        game_category: formData.game_category || null,
        reporting_time: formData.reporting_time || null,
        rsvp_deadline: formData.rsvp_deadline || null,
        // For internal games, don't send age_category, use age_categories
        age_category: formData.is_internal ? null : formData.age_category,
        age_categories: formData.is_internal ? formData.age_categories : null,
        opponent_team: formData.is_internal ? null : formData.opponent_team,
        team_id: formData.is_internal ? null : formData.team_id,
      };
      await api.post(`/tournaments/${tournament.id}/games`, payload);
      toast.success('Game created successfully!');
      setShowCreateDialog(false);
      fetchGames();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create game');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateGame = async () => {
    if (!selectedGame) return;
    
    setSubmitting(true);
    try {
      await api.put(`/tournaments/${tournament.id}/games/${selectedGame.id}`, {
        game_date: formData.game_date,
        start_time: formData.start_time,
        reporting_time: formData.reporting_time || null,
        rsvp_deadline: formData.rsvp_deadline || null,
        location: formData.location,
        location_url: formData.location_url || null,
        opponent_team: formData.opponent_team,
        game_type: formData.game_type,
        game_category: formData.game_category || null,
        notes: formData.notes,
        team_id: formData.team_id || null,
      });
      toast.success('Game updated successfully');
      setShowEditDialog(false);
      fetchGames();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update game');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteGame = async (gameId) => {
    setGameToDelete(gameId);
    setDeleteGameDialogOpen(true);
  };

  const confirmDeleteGame = async () => {
    if (!gameToDelete) return;
    try {
      await api.delete(`/tournaments/${tournament.id}/games/${gameToDelete}`);
      toast.success('Game deleted');
      setDeleteGameDialogOpen(false);
      setGameToDelete(null);
      fetchGames();
    } catch (error) {
      toast.error('Failed to delete game');
    }
  };

  const handleArchiveOldGames = async () => {
    const today = getTodayInTz(businessTz);
    setArchiving(true);
    try {
      const res = await api.post(`/tournaments/${tournament.id}/games/archive-old`, { before_date: today });
      const count = res.data.archived_count;
      if (count > 0) {
        toast.success(`Archived ${count} past game${count !== 1 ? 's' : ''}`);
        fetchGames();
      } else {
        toast.info('No past games to archive');
      }
      setShowArchiveDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive games');
    } finally {
      setArchiving(false);
    }
  };

  const pastGameCount = games.filter(g => !g.is_archived && g.game_date <= getTodayInTz(businessTz)).length;

  const openEditDialog = (game) => {
    setSelectedGame(game);
    setFormData({
      age_category: game.age_category,
      game_date: game.game_date,
      start_time: game.start_time,
      reporting_time: game.reporting_time || '',
      rsvp_deadline: game.rsvp_deadline ? game.rsvp_deadline.slice(0, 16) : '', // Format for datetime-local
      location: game.location,
      location_url: game.location_url || '',
      opponent_team: game.opponent_team,
      game_type: game.game_type,
      game_category: game.game_category || '',
      notes: game.notes || '',
      team_id: game.team_id || '',
    });
    setShowEditDialog(true);
  };

  const openRsvpDialog = async (game) => {
    setSelectedGame(game);
    try {
      const res = await api.get(`/tournaments/${tournament.id}/games/${game.id}/rsvps`);
      setGameRsvps(res.data);
      setShowRsvpDialog(true);
    } catch (error) {
      toast.error('Failed to load RSVPs');
    }
  };

  const openSelectPlayersDialog = async (game) => {
    setSelectedGame(game);
    try {
      const res = await api.get(`/tournaments/${tournament.id}/games/${game.id}/rsvps-with-counts`);
      setRsvpsWithCounts(res.data.rsvps || []);
      setSelectedPlayerIds(res.data.selected_players || []);
      setShowSelectPlayersDialog(true);
    } catch (error) {
      toast.error('Failed to load RSVPs with game counts');
    }
  };

  const handleSaveSelectedPlayers = async () => {
    if (!selectedGame) return;
    
    setSubmitting(true);
    try {
      await api.post(`/tournaments/${tournament.id}/games/${selectedGame.id}/select-players`, {
        player_ids: selectedPlayerIds
      });
      toast.success(`Selected ${selectedPlayerIds.length} players for this game`);
      setShowSelectPlayersDialog(false);
      fetchGames(); // Refresh to show updated selection count
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save selected players');
    } finally {
      setSubmitting(false);
    }
  };

  const [sendingNudge, setSendingNudge] = useState(false);
  const handleSendRsvpReminder = async () => {
    if (!selectedGame) return;
    // Target selected players who haven't firmly confirmed (either pending or tentative)
    const nudgeableIds = new Set(
      rsvpsWithCounts
        .filter((r) => r.response === 'not_responded' || r.response === 'tentative')
        .map((r) => r.player_id)
    );
    const targetIds = selectedPlayerIds.filter((pid) => nudgeableIds.has(pid));
    if (targetIds.length === 0) {
      toast.error('Select at least one player from the Not Responded or Tentative list.');
      return;
    }
    setSendingNudge(true);
    try {
      const { data } = await api.post(
        `/tournaments/${tournament.id}/games/${selectedGame.id}/send-rsvp-reminder`,
        { player_ids: targetIds }
      );
      toast.success(data.message || `Reminders sent to ${data.sent} parent(s).`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminders');
    } finally {
      setSendingNudge(false);
    }
  };

  const togglePlayerSelection = (playerId) => {
    setSelectedPlayerIds(prev => {
      if (prev.includes(playerId)) {
        return prev.filter(id => id !== playerId);
      } else {
        return [...prev, playerId];
      }
    });
  };

  const selectAllYesRsvps = () => {
    const yesPlayerIds = rsvpsWithCounts
      .filter(r => r.response === 'yes')
      .map(r => r.player_id);
    setSelectedPlayerIds(yesPlayerIds);
  };

  const clearAllSelections = () => {
    setSelectedPlayerIds([]);
  };

  const openResultDialog = (game) => {
    setSelectedGame(game);
    setGameResult(game.game_result || '');
    setGameResultNotes(game.game_result_notes || '');
    setShowResultDialog(true);
  };

  const handleSaveGameResult = async () => {
    if (!selectedGame) return;
    
    setSubmitting(true);
    try {
      await api.post(`/tournaments/${tournament.id}/games/${selectedGame.id}/result`, {
        result: gameResult,
        notes: gameResultNotes
      });
      toast.success(gameResult ? `Game marked as ${gameResult.replace('_', ' ')}` : 'Game result cleared');
      setShowResultDialog(false);
      fetchGames();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update game result');
    } finally {
      setSubmitting(false);
    }
  };

  const getResultBadge = (result) => {
    const badges = {
      win: { bg: 'bg-green-100', text: 'text-green-700', label: 'Won' },
      loss: { bg: 'bg-red-100', text: 'text-red-700', label: 'Lost' },
      tied: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Tied' },
      no_game: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'No Game' },
      postponed: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Postponed' }
    };
    return badges[result] || null;
  };

  const openManagePlayersDialog = async (game) => {
    setSelectedGame(game);
    setAddPlayerWarning(null);
    try {
      const res = await api.get(`/tournaments/${tournament.id}/games/${game.id}/available-players`);
      setAvailablePlayers(res.data.players || []);
      setShowManagePlayersDialog(true);
    } catch (error) {
      toast.error('Failed to load available players');
    }
  };

  const handleAddPlayerToGame = async (playerId, override = false) => {
    if (!selectedGame) return;
    
    setSubmitting(true);
    try {
      const res = await api.post(
        `/tournaments/${tournament.id}/games/${selectedGame.id}/add-player?player_id=${playerId}&override_team_check=${override}`
      );
      
      if (res.data.requires_override) {
        // Show warning and ask for confirmation
        setAddPlayerWarning({
          playerId,
          warning: res.data.warning,
          playerName: res.data.player_name,
          teamName: res.data.team_name
        });
      } else if (res.data.success) {
        toast.success(res.data.message);
        if (res.data.warning) {
          toast.warning(res.data.warning);
        }
        // Refresh available players
        const refreshRes = await api.get(`/tournaments/${tournament.id}/games/${selectedGame.id}/available-players`);
        setAvailablePlayers(refreshRes.data.players || []);
        // Refresh games to update RSVP counts
        fetchGames();
        setAddPlayerWarning(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add player');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemovePlayerFromGame = async (playerId, playerName) => {
    if (!selectedGame) return;
    
    if (!window.confirm(`Remove ${playerName} from this game?`)) {
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await api.delete(
        `/tournaments/${tournament.id}/games/${selectedGame.id}/players/${playerId}`
      );
      
      if (res.data.success) {
        toast.success(res.data.message);
        // Refresh RSVPs list
        const refreshRes = await api.get(`/tournaments/${tournament.id}/games/${selectedGame.id}/rsvps`);
        setGameRsvps(refreshRes.data);
        // Refresh games to update RSVP counts
        fetchGames();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove player');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkData.trim()) {
      toast.error('Please enter game data');
      return;
    }
    
    // Parse CSV data
    const lines = bulkData.trim().split('\n');
    const games = [];
    
    for (let i = 1; i < lines.length; i++) { // Skip header
      const parts = lines[i].split(',').map(p => p.trim());
      if (parts.length >= 6) {
        games.push({
          age_category: parts[0],
          game_date: parts[1],
          start_time: parts[2],
          location: parts[3],
          opponent_team: parts[4],
          game_type: parts[5].toLowerCase(),
          notes: parts[6] || '',
        });
      }
    }
    
    if (games.length === 0) {
      toast.error('No valid games found in the data');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await api.post(`/tournaments/${tournament.id}/games/bulk-upload`, games);
      toast.success(`${res.data.created} game(s) created, ${res.data.errors} error(s)`);
      setShowBulkUploadDialog(false);
      setBulkData('');
      fetchGames();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload games');
    } finally {
      setSubmitting(false);
    }
  };

  // Filter games by age category and archive status
  const filteredGames = games.filter(g => {
    if (ageFilter !== 'all' && g.age_category !== ageFilter) return false;
    if (!showArchived && g.is_archived) return false;
    return true;
  });

  const archivedCount = games.filter(g => g.is_archived).length;

  // Group games by date for better display
  const gamesByDate = filteredGames.reduce((acc, game) => {
    if (!acc[game.game_date]) {
      acc[game.game_date] = [];
    }
    acc[game.game_date].push(game);
    return acc;
  }, {});

  const formatDate = (dateStr) => {
    return formatDateOnlyDisplay(dateStr, {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      year: undefined,
    });
  };

  return (
    <div className="space-y-4">
      {/* Header with actions */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold">Game Schedule</h3>
          <div className="flex items-center gap-2">
            <Label className="text-sm">Age:</Label>
            <Select value={ageFilter} onValueChange={setAgeFilter}>
              <SelectTrigger className="w-28" data-testid="games-age-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {tournament?.age_categories?.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat.toUpperCase()}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {archivedCount > 0 && (
              <Button
                variant={showArchived ? "secondary" : "ghost"}
                size="sm"
                onClick={() => setShowArchived(!showArchived)}
                className="text-xs gap-1"
                data-testid="toggle-archived-btn"
              >
                <Archive className="h-3.5 w-3.5" />
                {showArchived ? `Hide Archived (${archivedCount})` : `Show Archived (${archivedCount})`}
              </Button>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {pastGameCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowArchiveDialog(true)}
              className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
              data-testid="archive-old-games-btn"
            >
              <Archive className="h-4 w-4 mr-1" />
              Archive Old ({pastGameCount})
            </Button>
          )}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => setShowBulkUploadDialog(true)}
            data-testid="bulk-upload-games-btn"
          >
            <Upload className="h-4 w-4 mr-1" />
            Bulk Upload
          </Button>
          <Button 
            size="sm" 
            onClick={() => {
              resetForm();
              setShowCreateDialog(true);
            }}
            data-testid="create-game-btn"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Game
          </Button>
        </div>
      </div>

      {/* Games List */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
        </div>
      ) : filteredGames.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <Calendar className="h-12 w-12 mx-auto mb-3 text-slate-300" />
          <p>{ageFilter === 'all' ? 'No games scheduled yet' : `No games for ${ageFilter.toUpperCase()}`}</p>
          <p className="text-sm">Click "Add Game" to create a game</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.keys(gamesByDate).sort().map(date => (
            <div key={date}>
              <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {formatDate(date)}
              </h4>
              <div className="space-y-2">
                {gamesByDate[date].map(game => (
                  <Card key={game.id} className="overflow-hidden">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          {/* Game Type Icon */}
                          <div className={`p-2 rounded-lg ${game.game_type === 'home' ? 'bg-green-100' : 'bg-blue-100'}`}>
                            {game.game_type === 'home' ? (
                              <Home className="h-5 w-5 text-green-600" />
                            ) : (
                              <Plane className="h-5 w-5 text-blue-600" />
                            )}
                          </div>
                          
                          {/* Game Info */}
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              {game.is_internal ? (
                                <span className="font-semibold text-amber-700">Internal Practice</span>
                              ) : (
                                <span className="font-semibold">vs {game.opponent_team}</span>
                              )}
                              {game.is_internal && game.age_categories ? (
                                game.age_categories.map(cat => (
                                  <Badge key={cat} variant="outline" className="uppercase text-xs">
                                    {cat}
                                  </Badge>
                                ))
                              ) : (
                                <Badge variant="outline" className="uppercase text-xs">
                                  {game.age_category}
                                </Badge>
                              )}
                              {game.is_internal && (
                                <Badge className="text-xs bg-amber-100 text-amber-700">
                                  Internal
                                </Badge>
                              )}
                              {game.game_category && (
                                <Badge className={`text-xs ${GAME_CATEGORY_COLORS[game.game_category] || 'bg-slate-100 text-slate-700'}`}>
                                  {GAME_CATEGORIES.find(c => c.value === game.game_category)?.label || game.game_category}
                                </Badge>
                              )}
                              {game.team_name && (
                                <Badge className="bg-blue-100 text-blue-700 text-xs">
                                  <Shield className="h-3 w-3 mr-1" />
                                  {game.team_name}
                                </Badge>
                              )}
                              {game.rsvp_locked && (
                                <Badge className="bg-red-100 text-red-700 text-xs">
                                  <Lock className="h-3 w-3 mr-1" />
                                  RSVPs Closed
                                </Badge>
                              )}
                              {game.game_result && getResultBadge(game.game_result) && (
                                <Badge className={`${getResultBadge(game.game_result).bg} ${getResultBadge(game.game_result).text} text-xs`}>
                                  <Trophy className="h-3 w-3 mr-1" />
                                  {getResultBadge(game.game_result).label}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {game.start_time}
                                {game.reporting_time && (
                                  <span className="text-xs text-slate-400">(Report: {game.reporting_time})</span>
                                )}
                              </span>
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {game.location_url ? (
                                  <a 
                                    href={game.location_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 hover:underline"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {game.location}
                                  </a>
                                ) : (
                                  game.location
                                )}
                              </span>
                              {game.rsvp_deadline && !game.rsvp_locked && (
                                <span className="text-xs text-amber-600">
                                  RSVP by: {new Date(game.rsvp_deadline).toLocaleString('en-US', { timeZone: businessTz })}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* RSVP Summary & Actions */}
                        <div className="flex items-center gap-4">
                          {/* RSVP Summary */}
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1 text-sm">
                              <CheckCircle className="h-4 w-4 text-green-600" />
                              <span>{game.rsvp_summary?.yes || 0}</span>
                            </div>
                            <div className="flex items-center gap-1 text-sm">
                              <XCircle className="h-4 w-4 text-red-600" />
                              <span>{game.rsvp_summary?.no || 0}</span>
                            </div>
                            <div className="flex items-center gap-1 text-sm">
                              <HelpCircle className="h-4 w-4 text-yellow-600" />
                              <span>{game.rsvp_summary?.tentative || 0}</span>
                            </div>
                            {game.rsvp_summary?.pending > 0 && (
                              <span className="text-sm text-slate-400">
                                ({game.rsvp_summary.pending} pending)
                              </span>
                            )}
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openManagePlayersDialog(game)}
                              title="Manage Players"
                              data-testid={`manage-players-${game.id}`}
                            >
                              <UserPlus className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openRsvpDialog(game)}
                              title="View RSVPs"
                              data-testid={`view-rsvps-${game.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openSelectPlayersDialog(game)}
                              title="Select Players"
                              className={game.selected_players_count > 0 ? "text-green-600" : ""}
                              data-testid={`select-players-${game.id}`}
                            >
                              <Users className="h-4 w-4" />
                              {game.selected_players_count > 0 && (
                                <span className="absolute -top-1 -right-1 bg-green-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                                  {game.selected_players_count}
                                </span>
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openResultDialog(game)}
                              title="Set Game Result"
                              className={game.game_result ? "text-blue-600" : ""}
                              data-testid={`game-result-${game.id}`}
                            >
                              <Flag className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditDialog(game)}
                              title="Edit Game"
                              data-testid={`edit-game-${game.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteGame(game.id)}
                              title="Delete Game"
                              className="text-red-500 hover:text-red-700"
                              data-testid={`delete-game-${game.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                      {game.notes && (
                        <p className="text-sm text-slate-500 mt-2 pl-14">{game.notes}</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Game Dialog */}
      <CreateGameDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        tournament={tournament}
        formData={formData}
        setFormData={setFormData}
        availableTeams={availableTeams}
        onCreate={handleCreateGame}
        submitting={submitting}
      />

      <EditGameDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        selectedGame={selectedGame}
        formData={formData}
        setFormData={setFormData}
        teams={teams}
        onUpdate={handleUpdateGame}
        submitting={submitting}
      />

      {/* View RSVPs Dialog */}
      <GameRsvpsDialog
        open={showRsvpDialog}
        onOpenChange={setShowRsvpDialog}
        selectedGame={selectedGame}
        gameRsvps={gameRsvps}
        rsvpIcons={RSVP_ICONS}
        rsvpColors={RSVP_COLORS}
        onRemovePlayer={handleRemovePlayerFromGame}
        submitting={submitting}
      />

      {/* Select Players Dialog */}
      <SelectPlayersDialog
        open={showSelectPlayersDialog}
        onOpenChange={setShowSelectPlayersDialog}
        selectedGame={selectedGame}
        rsvpsWithCounts={rsvpsWithCounts}
        selectedPlayerIds={selectedPlayerIds}
        setSelectedPlayerIds={setSelectedPlayerIds}
        togglePlayerSelection={togglePlayerSelection}
        selectAllYesRsvps={selectAllYesRsvps}
        clearAllSelections={clearAllSelections}
        onSaveSelectedPlayers={handleSaveSelectedPlayers}
        onSendRsvpReminder={handleSendRsvpReminder}
        submitting={submitting}
        sendingNudge={sendingNudge}
      />

      {/* Game Result Dialog */}
      <GameResultDialog
        open={showResultDialog}
        onOpenChange={setShowResultDialog}
        selectedGame={selectedGame}
        gameResult={gameResult}
        setGameResult={setGameResult}
        gameResultNotes={gameResultNotes}
        setGameResultNotes={setGameResultNotes}
        onSave={handleSaveGameResult}
        submitting={submitting}
      />

      {/* Bulk Upload Dialog */}
      <BulkUploadGamesDialog
        open={showBulkUploadDialog}
        onOpenChange={setShowBulkUploadDialog}
        tournament={tournament}
        bulkData={bulkData}
        setBulkData={setBulkData}
        onUpload={handleBulkUpload}
        submitting={submitting}
      />

      {/* Delete Game Confirmation Dialog */}
      <AlertDialog open={deleteGameDialogOpen} onOpenChange={setDeleteGameDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Game</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this game? All RSVPs will also be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteGame}
              className="bg-rose-500 hover:bg-rose-600"
            >
              Delete Game
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Archive Old Games Confirmation */}
      <AlertDialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive Old Games</AlertDialogTitle>
            <AlertDialogDescription>
              This will archive {pastGameCount} game{pastGameCount !== 1 ? 's' : ''} with dates before today. 
              Archived games will be marked as completed and hidden from the active view. You can unarchive them later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleArchiveOldGames}
              disabled={archiving}
              className="bg-amber-500 hover:bg-amber-600"
              data-testid="confirm-archive-btn"
            >
              {archiving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Archive className="h-4 w-4 mr-1" />}
              Archive {pastGameCount} Game{pastGameCount !== 1 ? 's' : ''}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ManagePlayersDialog
        open={showManagePlayersDialog}
        onOpenChange={setShowManagePlayersDialog}
        selectedGame={selectedGame}
        availablePlayers={availablePlayers}
        addPlayerWarning={addPlayerWarning}
        setAddPlayerWarning={setAddPlayerWarning}
        onAddPlayer={handleAddPlayerToGame}
        submitting={submitting}
      />
    </div>
  );
};

export default TournamentGames;
